import { useUiLanguage } from '@/i18n/useUiLanguage';
import { tr } from '@/i18n/copy';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BookOpen, CheckCircle2 } from 'lucide-react';
import TpsBrand from '@/components/TpsBrand';
import TpsGuideLink, { GUIDE_EDITIONS, guideForLanguage } from '@/components/TpsGuideLink';
import { publicLegalDocuments, useCommercialTerms } from '@/components/CommercialTerms';
import copy from './publicSiteCopy';

function useCopy() {
  const language = useUiLanguage();
  return copy[language?.split('-')[0]] || copy.it;
}

export function PublicNavigation() {
  const c = useCopy();
  return <nav className="tps-site-nav" aria-label={c.navigation}>
    <div>{[['prodotto', c.product], ['chi-siamo', c.about], ['esperienze', c.experiences], ['percorso', c.journey], ['piani', c.plans], ['risorse', c.guide], ['faq', 'FAQ']].map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}</div>
  </nav>;
}

export function FounderStory() {
  const c = useCopy();
  return <article className="tps-origin-story" id="storia" aria-labelledby="tps-story-title">
    <header><p className="tps-eyebrow">{c.founderRole}</p><h3 id="tps-story-title">{c.founderTitle}</h3><div className="tps-story-line" aria-hidden="true" /></header>
    <div className="tps-narrative"><p>{c.founderText}</p><p>{c.founderVision}</p><h4>{c.storyBridge}</h4><p>{c.storyFlow}</p><p>{c.storyIdentity}</p><p className="tps-story-closing">{c.storyClose}</p></div>
  </article>;

}

export function PublicResources() {
  const c = useCopy();
  const language = useUiLanguage();
  const edition = guideForLanguage(language);
  return <section className="tps-section tps-resources" id="risorse" aria-labelledby="tps-resources-title"><div className="tps-section-inner tps-story-grid">
    <div><p className="tps-eyebrow">{c.resources}</p><h2 id="tps-resources-title">{c.guideTitle}</h2><p className="tps-intro">{c.guideText}</p><TpsGuideLink className="tps-resource-download" /><p className="tps-guide-language">{c.guideLanguage}</p><nav className="tps-guide-editions" aria-label={c.guideEditions}>{GUIDE_EDITIONS.map(item => <a key={item.code} href={item.href} download={item.filename} hrefLang={item.code} lang={item.code} aria-current={item.code === edition.code ? 'true' : undefined}>{item.name}<ArrowUpRight size={13} aria-hidden="true" /></a>)}</nav></div>
    <div className="tps-guide-card"><BookOpen size={32} aria-hidden="true" /><p className="tps-eyebrow">Tattoo Pro Studio</p><h3>{c.guideCard}</h3><ul>{[c.guideOne, c.guideTwo, c.guideThree].map(item => <li key={item}><CheckCircle2 size={17} aria-hidden="true" />{item}</li>)}</ul><span className="tps-guide-format">PDF · {edition.name}</span></div>
  </div></section>;
}

export function ExtraQuestions() {
  const c = useCopy();
  return <>{c.questions.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}</>;
}

export function PublicFooter() {
  const c = useCopy();
  const { docs, error, retry } = useCommercialTerms();
  const operator = docs?.production_ready || docs?.early_access ? docs.operator : null;
  const email = operator?.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(operator.email) ? operator.email : '';
  return <footer className="tps-site-footer" id="contatti"><div className="tps-section-inner">
    <div className="tps-footer-grid"><div><TpsBrand /><p className="tps-footer-description">{c.footerText}</p><a className="tps-footer-back" href="#inizio">{c.backTop}<ArrowUpRight size={16} aria-hidden="true" /></a></div>
      <nav aria-label={c.explore}><h2>{c.explore}</h2><a href="#chi-siamo">{c.about}</a><a href="#storia">{c.founder}</a><a href="#esperienze">{c.experiences}</a><a href="#piani">{c.plans}</a><a href="#faq">FAQ</a></nav>
      <nav aria-label={c.start}><h2>{c.start}</h2><Link to="/register">{tr('Prova gratis 14 giorni')}</Link><Link to="/login">{tr('Accedi')}</Link><a href="#risorse">{c.guide}</a></nav>
      <nav aria-label={c.documents}><h2>{c.documents}</h2>{publicLegalDocuments(docs, c).map(([id, label]) => <Link key={id} to={`/legal#${id}`}>{label}</Link>)}</nav>
    </div>
    <div className="tps-footer-bottom">
      <p>© {new Date().getFullYear()} Tattoo Pro Studio{operator?.tax_id && <><span aria-hidden="true"> · </span><span className="tps-footer-vat">{c.vatLabel} {operator.tax_id}</span></>}</p>
      {operator ? <details className="tps-footer-provider">
        <summary>{c.providerDetails}</summary>
        <address><span>{operator.name}</span><span>{operator.address}</span>{email && <a href={`mailto:${email}`}>{email}</a>}</address>
      </details> : error ? <p role="status">{c.contactError} <button type="button" onClick={retry}>{tr('Riprova')}</button></p> : <Link to="/legal#operator">{c.company}</Link>}
    </div>
  </div></footer>;
}


